<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { X } from 'lucide-vue-next'
import type { ToastAction, ToastVariant } from '@/composables/useToast'

interface Props {
  message: string
  variant: ToastVariant
  /** Optional single follow-up (undo, #3694), rendered as a button before the close control. */
  action?: ToastAction
}

const props = defineProps<Props>()

defineEmits<{ (e: 'dismiss'): void; (e: 'action'): void }>()

const { t } = useI18n()

// Every toast carries role="alert" so there is one predictable hook for both
// assistive tech and the E2E suite. An explicit aria-live overrides the role's
// implicit "assertive": only errors are allowed to interrupt what the user is
// doing, the rest wait for a pause (#1501).
const politeness = computed(() => (props.variant === 'error' ? 'assertive' : 'polite'))
</script>

<template>
  <div :class="['toast', `toast--${variant}`]" role="alert" :aria-live="politeness">
    <span class="toast__message">{{ message }}</span>
    <button
      v-if="action"
      type="button"
      class="toast__action"
      @click="$emit('action')"
    >
      {{ action.label }}
    </button>
    <button
      type="button"
      class="toast__close"
      :aria-label="t('common.close')"
      @click="$emit('dismiss')"
    >
      <X :size="16" />
    </button>
  </div>
</template>

<style scoped>
/* Variant classes like .toast--error are applied dynamically via :class. */
.toast {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--radius-base);
  border: 1px solid;
  box-shadow: var(--shadow-lg);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  /* The container is click-through so it never swallows page clicks; the
     toasts themselves must still be interactive. */
  pointer-events: auto;
}

.toast__message {
  flex: 1;
  /* A long message wraps instead of stretching the toast past its max-width. */
  min-width: 0;
  overflow-wrap: anywhere;
}

/* Reads as a link rather than a second button: the toast already carries the
   outcome, and the action is the exception the user takes from it. `currentColor`
   keeps it legible on every variant's fill without a colour of its own. */
.toast__action {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: var(--font-weight-semibold);
  text-decoration: underline;
  cursor: pointer;
}

.toast__action:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.toast__close {
  display: flex;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  line-height: 1;
  cursor: pointer;
  opacity: 0.7;
}

.toast__close:hover {
  opacity: 1;
}

.toast__close:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

/* Variants mirror Alert.vue, but on the solid `-light` backgrounds: a toast
   floats over arbitrary page content, so Alert's translucent backgrounds would
   let that content show through. Text uses the darker token of each family to
   stay legible on the solid fill (WCAG AA). */
.toast--success {
  background-color: var(--color-semantic-success-light);
  color: var(--color-semantic-success-dark);
  border-color: var(--color-semantic-success-dark);
}

.toast--error {
  background-color: var(--color-semantic-error-light);
  color: var(--color-semantic-error);
  border-color: var(--color-semantic-error);
}

.toast--warning {
  background-color: var(--color-semantic-warning-light);
  color: var(--color-semantic-warning-text);
  border-color: var(--color-semantic-warning);
}

.toast--info {
  background-color: var(--color-semantic-info-light);
  color: var(--color-semantic-info);
  border-color: var(--color-semantic-info);
}
</style>
